import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Validators}                                                from '@angular/forms';
import {
  ApDynformsConfigFieldset
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigTextbox
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {ApDynformsValidator}                                       from '../../ap-dynforms/ap-dynforms-validator';
import {
  ApDynformsConfigComboBox
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynformsComponent}                                       from '../../ap-dynforms/ap-dynforms.component';
import {map}                                                       from 'rxjs/operators';
import {InstrumentStore}                                           from '../../stores/docu/instrument.store';
import {
  ApDynformsConfigNumerictextbox
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {WorkTypesStore}                                            from '../../stores/base-data/worrktypes.store';
import {ListSortOrder, ObjectFactory}                              from 'ts-tooling';
import {FormStore}                                                 from '../../stores/layout/form.store';
import {LoginStore}                                                from '../../stores/login/login.store';
import {BehaviorSubject, combineLatest, Observable, Subscription}  from 'rxjs';
import {
  ApDynformsConfigCheckbox
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-checkbox';
import {ApDateService}                                             from '../../ap-core/services/ap-date-service';
import {nameOf}                                                    from '../../ap-core/utils/ap-name-of';
import {InstrumentEditFormData}                                    from './types/ap-instrument-edit.types';
import {
  ResourcesDataService
}                                                                  from '../../services/data/resources/resources-data.service';
import {MachineStore}                                              from '../../stores/docu/machine.store';
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import IWorktypes = Data.BaseData.IWorktypes;

/**
 * Component for editing or creating an instrument.
 */
@Component({
  selector: 'ap-instruments-edit',
  template: `
    <ap-dynforms [fieldsets]="formConfig" [caption]="caption" [loading$]="loading$">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_instruments_edit_cancel"
                type="button"
                class="k-button k-primary ap-form-button-left"
                (click)="onCancelClick()">{{ 'Global__Cancel' | translate }}
        </button>
        <button id="button_instruments_edit_save"
                type="button"
                class="k-button k-primary ap-form-button-right"
                [disabled]="(isSaveDisabled$ | async)"
                (click)="onSubmitClick()">{{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>`,
})
export class ApInstrumentsEditComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  public data: IInstrument | undefined;
  public caption: string;
  public formConfig: ApDynformsConfigFieldset[];
  public isNewMode = false;
  public loading$: Observable<boolean>;
  public isSaveDisabled$ = new BehaviorSubject<boolean>(true);

  private _subscriptions: Subscription[] = [];
  private _formData: InstrumentEditFormData | undefined;

  constructor(private formStore: FormStore,
              private loginStore: LoginStore,
              private machineStore: MachineStore,
              private worktypeStore: WorkTypesStore,
              private instrumentsStore: InstrumentStore,
              private dateService: ApDateService,
              private resourcesDataService: ResourcesDataService) {
  }

  /**
   * when the component is loaded
   */
  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.worktypeStore.Loading$,
      this.machineStore.Loading$,
      this.instrumentsStore.Listen(s => s.saving)
    ]).pipe(
      map(loadings => loadings.some(loading => loading))
    );
    this.isNewMode = !this.data;
    this._formData = {
      Name: this.data?.Name,
      DefaultWidth: this.data?.DefaultWidth,
      DefaultWorktype: this.data?.DefaultWorktype?.Id,
      IsActive: this.resourcesDataService.isResourceActive(this.data)
    };
    this.caption = this.isNewMode ? 'Global_Create_Instrument' : 'Global_Edit_Instrument';
    this.formBuilder();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      this.instrumentsStore.Listen(s => s.saved).subscribe(saved => {
        if (saved) {
          this.formStore.closeForm();
        }
      })
    );
    this._subscriptions.push(
      this.dynForm.FormValues$.subscribe(() => {
        this.dynForm.form?.updateValueAndValidity();
        const formValues = this.dynForm.form?.getRawValue() as InstrumentEditFormData;
        const isSaveAvailable = JSON.stringify(formValues) !== JSON.stringify(this._formData)
          && !!this.dynForm.form?.valid;
        this.isSaveDisabled$.next(!isSaveAvailable);
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x?.unsubscribe());
  }

  /**
   * Submitting the changes.
   * Either creating a new object or updating the current.
   */
  public onSubmitClick(): void {
    const instrument = this.data ?? ({} as IInstrument);
    const formData = this.dynForm.form.getRawValue() as InstrumentEditFormData;
    if (!formData) {
      return;
    }
    const worktype = this.worktypeStore.WorkTypes.find(x => x.Id === formData.DefaultWorktype);
    instrument.FarmId = this.loginStore.SelectedFarmId;
    instrument.DefaultWorktype = worktype;
    instrument.Type = worktype?.Type;
    instrument.IsDeleted = !formData.IsActive;
    instrument.DeletedAt = formData.IsActive ? undefined : this.dateService.getUtc();
    instrument.DeletedBy = formData.IsActive ? undefined : this.loginStore.User.Id;
    if (this.isNewMode) {
      this.instrumentsStore.createInstrument(instrument);
    } else {
      this.instrumentsStore.updateInstrument(instrument);
    }
  }

  /**
   * Cancel a clicked eventHandler - closes form.
   */
  public onCancelClick(): void {
    this.formStore.closeForm();
  }

  /**
   * binding of validators
   */
  private formBuilder(): void {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'instruments',
        config: [
          new ApDynformsConfigTextbox({
            key: nameOf<InstrumentEditFormData>('Name'),
            label: 'Docu_Ini__Description',
            value: this._formData.Name,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<InstrumentEditFormData>('DefaultWidth'),
            label: 'Docu_Ini__WorkingWidth',
            value: this._formData.DefaultWidth,
            min: 0,
            max: 100,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              }),
              new ApDynformsValidator({
                validator: Validators.max(48),
                errorKey: 'Text_ValueInvalid'
              }),
              new ApDynformsValidator({
                validator: Validators.min(0),
                errorKey: 'Text_ValueInvalid'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<InstrumentEditFormData>('DefaultWorktype'),
            label: 'Docu_Ini__WorkType',
            value: this._formData.DefaultWorktype,
            valueField: nameOf<IWorktypes>('Id'),
            textField: nameOf<IWorktypes>('Description'),
            options: this.worktypeStore.WorkTypes$.pipe(
              map((d) => ObjectFactory.Copy(d).SortBy([nameOf<IWorktypes>('Description')], [ListSortOrder.ASC]))
            ),
            valuePrimitive: true,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigCheckbox({
            key: nameOf<InstrumentEditFormData>('IsActive'),
            label: 'Global__Active',
            value: this._formData.IsActive,
            disabled$: this.machineStore.Machines$.pipe(
              map(machines => machines.some(x => !!x.DefaultInstrument && x.DefaultInstrument?.Id === this.data?.Id))
            )
          })
        ]
      })
    ];
  }
}
